import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Map, List, Heart, ChevronLeft, ChevronRight, MapPin, Navigation, X } from 'lucide-react';
import { motion } from 'framer-motion';
import SpaCard from '../components/SpaCard';
import MapView from '../components/MapView';
import Subscribe from '../components/Subscribe';

import { useFavorites } from '../context/FavoritesContext';
import { ALL_SPAS, AREAS, SPA_TYPES, calculateDistance, getNearbySpas } from '../data/spaData';
import useLocation from '../hooks/useLocation';

const SpaListings = () => {
    const [searchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState('list');
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(searchParams.get('favorites') === 'true');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);
    const [sortByDistance, setSortByDistance] = useState(false);

    // Filter states
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [selectedArea, setSelectedArea] = useState(searchParams.get('area') || '');
    const [selectedType, setSelectedType] = useState(searchParams.get('type') || '');
    const [selectedRating, setSelectedRating] = useState('');

    const { favorites } = useFavorites();
    const { location, loading: locationLoading, requestLocation, hasLocation, clearLocation } = useLocation();

    // Filter spas based on criteria
    const filteredSpas = useMemo(() => {
        let result = [...ALL_SPAS];

        // Filter by favorites if enabled
        if (showFavoritesOnly) {
            result = result.filter(spa => favorites.includes(spa.id));
        }

        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(spa =>
                spa.name.toLowerCase().includes(query) ||
                spa.area.toLowerCase().includes(query) ||
                spa.spa_type.toLowerCase().includes(query) ||
                spa.description.toLowerCase().includes(query)
            );
        }

        // Filter by area
        if (selectedArea) {
            result = result.filter(spa => spa.area === selectedArea);
        }

        // Filter by spa type
        if (selectedType) {
            result = result.filter(spa => spa.spa_type === selectedType);
        }

        // Filter by rating
        if (selectedRating) {
            result = result.filter(spa => spa.rating >= parseFloat(selectedRating));
        }

        // Add distance if user location is available
        if (location) {
            result = result.map(spa => ({
                ...spa,
                distance: calculateDistance(location.latitude, location.longitude, spa.latitude, spa.longitude)
            }));

            // Sort by distance if enabled
            if (sortByDistance) {
                result.sort((a, b) => a.distance - b.distance);
            }
        }

        return result;
    }, [showFavoritesOnly, favorites, searchQuery, selectedArea, selectedType, selectedRating, location, sortByDistance]);

    // Get nearby spas (within 20km for Bangalore)
    const nearbySpas = useMemo(() => {
        if (!location) return [];
        return getNearbySpas(location.latitude, location.longitude, 20).slice(0, 6);
    }, [location]);

    // Paginate results
    const paginatedSpas = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredSpas.slice(start, start + pageSize);
    }, [filteredSpas, currentPage, pageSize]);

    const totalCount = filteredSpas.length;
    const totalPages = Math.ceil(totalCount / pageSize);

    // Reset filters
    const handleFilterChange = useCallback((setter) => (value) => {
        setter(value);
        setCurrentPage(1);
    }, []);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="spa-listings-page">
            <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8"
                >
                    <h1 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D] mb-2">
                        {showFavoritesOnly ? 'Your Favorite Spas' : 'Explore Spas in Bangalore'}
                    </h1>
                    <p className="text-[#4A4E4D]/60">
                        {showFavoritesOnly 
                            ? `${totalCount} favorite ${totalCount === 1 ? 'spa' : 'spas'}`
                            : 'Discover the best spas and wellness centers with 3+ star ratings'
                        }
                    </p>
                </motion.div>

                {/* Location Detection Banner */}
                {!hasLocation && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-gradient-to-r from-[#F5E1E4] to-[#F9F6F0] rounded-2xl border border-[#EBCED3]"
                    >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#722F37]/10 rounded-full flex items-center justify-center">
                                    <Navigation className="w-5 h-5 text-[#722F37]" />
                                </div>
                                <div>
                                    <p className="font-medium text-[#4A4E4D]">Find spas near you</p>
                                    <p className="text-sm text-[#4A4E4D]/60">Enable location to see nearby spas and distances</p>
                                </div>
                            </div>
                            <button
                                onClick={requestLocation}
                                disabled={locationLoading}
                                className="px-5 py-2.5 bg-[#722F37] text-white rounded-full text-sm font-medium hover:bg-[#8B3D47] transition-colors disabled:opacity-50 flex items-center gap-2"
                                data-testid="enable-location-btn"
                            >
                                {locationLoading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Detecting...
                                    </>
                                ) : (
                                    <>
                                        <MapPin className="w-4 h-4" />
                                        Enable Location
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                )}

                {/* Nearby Spas Section */}
                {hasLocation && nearbySpas.length > 0 && !showFavoritesOnly && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-[#722F37]" />
                                <h2 className="font-heading text-2xl text-[#4A4E4D]">Near You</h2>
                                <span className="text-sm text-[#4A4E4D]/50">({nearbySpas.length} within 20km)</span>
                            </div>
                            <button
                                onClick={clearLocation}
                                className="text-sm text-[#4A4E4D]/60 hover:text-[#722F37] flex items-center gap-1"
                            >
                                <X className="w-4 h-4" />
                                Clear location
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {nearbySpas.slice(0, 3).map((spa) => (
                                <div key={spa.id} className="relative">
                                    <SpaCard spa={spa} variant="featured" />
                                    <div className="absolute top-4 left-4 px-2 py-1 bg-[#722F37] text-white text-xs font-medium rounded-full">
                                        {spa.distance.toFixed(1)} km away
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Filters */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="mb-6"
                >
                    {/* Search */}
                    <div className="bg-white rounded-2xl p-4 sm:p-6 border border-[#E0DAD0] shadow-sm mb-4">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
                            placeholder="Search spas by name, area, or type..."
                            className="w-full px-4 py-3 bg-[#F9F6F0] border border-[#E0DAD0] rounded-xl text-[#4A4E4D] placeholder:text-[#4A4E4D]/40 focus:outline-none focus:ring-2 focus:ring-[#C8A86B]/30"
                            data-testid="search-input"
                        />
                    </div>

                    {/* Filter Dropdowns */}
                    <div className="bg-white rounded-2xl p-4 sm:p-6 border border-[#E0DAD0] shadow-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-sm font-medium text-[#4A4E4D]">Filters:</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {/* Area Filter */}
                            <select
                                value={selectedArea}
                                onChange={(e) => handleFilterChange(setSelectedArea)(e.target.value)}
                                className="px-4 py-2.5 bg-[#F9F6F0] border border-[#E0DAD0] rounded-xl text-sm text-[#4A4E4D] focus:outline-none focus:ring-2 focus:ring-[#C8A86B]/30"
                                data-testid="area-filter"
                            >
                                <option value="">All Areas</option>
                                {AREAS.map(area => (
                                    <option key={area} value={area}>{area}</option>
                                ))}
                            </select>

                            {/* Type Filter */}
                            <select
                                value={selectedType}
                                onChange={(e) => handleFilterChange(setSelectedType)(e.target.value)}
                                className="px-4 py-2.5 bg-[#F9F6F0] border border-[#E0DAD0] rounded-xl text-sm text-[#4A4E4D] focus:outline-none focus:ring-2 focus:ring-[#C8A86B]/30"
                                data-testid="type-filter"
                            >
                                <option value="">All Types</option>
                                {SPA_TYPES.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>

                            {/* Rating Filter */}
                            <select
                                value={selectedRating}
                                onChange={(e) => handleFilterChange(setSelectedRating)(e.target.value)}
                                className="px-4 py-2.5 bg-[#F9F6F0] border border-[#E0DAD0] rounded-xl text-sm text-[#4A4E4D] focus:outline-none focus:ring-2 focus:ring-[#C8A86B]/30"
                                data-testid="rating-filter"
                            >
                                <option value="">All Ratings</option>
                                <option value="4.5">4.5+ Stars</option>
                                <option value="4">4+ Stars</option>
                                <option value="3.5">3.5+ Stars</option>
                            </select>
                        </div>

                        {/* Sort by distance toggle */}
                        {hasLocation && (
                            <div className="mt-4 pt-4 border-t border-[#E0DAD0]">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={sortByDistance}
                                        onChange={(e) => setSortByDistance(e.target.checked)}
                                        className="w-4 h-4 rounded border-[#E0DAD0] text-[#722F37] focus:ring-[#722F37]"
                                    />
                                    <span className="text-sm text-[#4A4E4D]">Sort by distance (nearest first)</span>
                                </label>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* View Toggle & Favorites Toggle */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="flex flex-wrap items-center justify-between gap-4 mb-6"
                >
                    <button
                        onClick={() => {
                            setShowFavoritesOnly(!showFavoritesOnly);
                            setCurrentPage(1);
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            showFavoritesOnly
                                ? 'bg-[#722F37] text-white'
                                : 'bg-white border border-[#E0DAD0] text-[#4A4E4D] hover:border-[#722F37]'
                        }`}
                        data-testid="toggle-favorites"
                    >
                        <Heart className={`w-4 h-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                        {showFavoritesOnly ? 'Showing Favorites' : 'Show Favorites Only'}
                    </button>

                    <div className="flex items-center gap-2 bg-white rounded-full p-1 border border-[#E0DAD0]">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                viewMode === 'list'
                                    ? 'bg-[#C8A86B] text-white'
                                    : 'text-[#4A4E4D] hover:bg-[#F5E1E4]'
                            }`}
                            data-testid="view-list"
                        >
                            <List className="w-4 h-4" />
                            List
                        </button>
                        <button
                            onClick={() => setViewMode('map')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                viewMode === 'map'
                                    ? 'bg-[#C8A86B] text-white'
                                    : 'text-[#4A4E4D] hover:bg-[#F5E1E4]'
                            }`}
                            data-testid="view-map"
                        >
                            <Map className="w-4 h-4" />
                            Map
                        </button>
                    </div>
                </motion.div>

                {/* Content */}
                {viewMode === 'map' ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <MapView spas={filteredSpas} height="600px" />
                        <p className="text-center text-[#4A4E4D]/60 mt-4 text-sm">
                            Showing {filteredSpas.length} spas on map
                        </p>
                    </motion.div>
                ) : paginatedSpas.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-16"
                    >
                        <div className="w-20 h-20 mx-auto mb-6 bg-[#F5E1E4] rounded-full flex items-center justify-center">
                            <Heart className="w-10 h-10 text-[#722F37]/30" />
                        </div>
                        <h3 className="font-heading text-2xl text-[#4A4E4D] mb-2">
                            {showFavoritesOnly ? 'No favorites yet' : 'No spas found'}
                        </h3>
                        <p className="text-[#4A4E4D]/60 mb-6">
                            {showFavoritesOnly 
                                ? 'Start exploring and save your favorite spas!' 
                                : 'Try adjusting your filters to find more spas.'}
                        </p>
                        {showFavoritesOnly && (
                            <button
                                onClick={() => setShowFavoritesOnly(false)}
                                className="btn-gold"
                                data-testid="explore-spas-btn"
                            >
                                Explore All Spas
                            </button>
                        )}
                    </motion.div>
                ) : (
                    <>
                        <div className="space-y-6">
                            {paginatedSpas.map((spa, index) => (
                                <motion.div
                                    key={spa.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.03 }}
                                    className="relative"
                                >
                                    <SpaCard spa={spa} />
                                    {spa.distance !== undefined && (
                                        <div className="absolute top-3 left-3 px-2 py-1 bg-[#722F37] text-white text-xs font-medium rounded-full sm:hidden">
                                            {spa.distance.toFixed(1)} km
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-12">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg bg-white border border-[#E0DAD0] text-[#4A4E4D] disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#C8A86B] transition-colors"
                                    data-testid="prev-page"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let page;
                                        if (totalPages <= 5) {
                                            page = i + 1;
                                        } else if (currentPage <= 3) {
                                            page = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            page = totalPages - 4 + i;
                                        } else {
                                            page = currentPage - 2 + i;
                                        }
                                        
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                className={`w-10 h-10 rounded-lg font-medium transition-all ${
                                                    currentPage === page
                                                        ? 'bg-[#C8A86B] text-white'
                                                        : 'bg-white border border-[#E0DAD0] text-[#4A4E4D] hover:border-[#C8A86B]'
                                                }`}
                                                data-testid={`page-${page}`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                    
                                    {totalPages > 5 && currentPage < totalPages - 2 && (
                                        <>
                                            <span className="px-2 text-[#4A4E4D]/50">...</span>
                                            <button
                                                onClick={() => handlePageChange(totalPages)}
                                                className="w-10 h-10 rounded-lg bg-white border border-[#E0DAD0] text-[#4A4E4D] hover:border-[#C8A86B] font-medium transition-all"
                                            >
                                                {totalPages}
                                            </button>
                                        </>
                                    )}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg bg-white border border-[#E0DAD0] text-[#4A4E4D] disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#C8A86B] transition-colors"
                                    data-testid="next-page"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}

                        <p className="text-center text-[#4A4E4D]/50 text-sm mt-4">
                            Page {currentPage} of {totalPages} ({totalCount} spas)
                        </p>
                    </>
                )}
            </div>

            {/* Subscribe Section */}
            <Subscribe />
        </div>
    );
};

export default SpaListings;
